import { useEffect, useState } from 'react';
import { FieldTypeEnum } from '../../../../enums/fieldTypeEnum'
import NumericForm from './NumericForm';
import { FormHelperText } from "@mui/material";
import TextForm from './TextForm';
import RelationForm from './RelationForm';
import { DropdownForm } from './DropdownForm';
import { M_InputTextField, M_Select, M_SwitchButton } from '../../../../M_Components';
import { useTranslation } from 'react-i18next';
import { genericEntityService } from '../../../../services/genericEntityService';
import { BooleanForm } from './BooleanForm';
import { TextFieldSizeEnum } from "../../../../enums/textFieldSizeEnum";
import { DateForm } from './DateForm';
import { AttachmentForm } from './AttachmentForm';
import { CurrencyForm } from './CurrencyForm';
import { DatetimeForm } from './DatetimeForm';
import { TimeForm } from './TimeForm';

const MAX_FIELD_CODE_LENGTH = 64;
const MAX_FIELD_CAPTION_LENGTH = 255;
const RELATIONAL_FIELDS = [FieldTypeEnum.Relation];

export const FieldForm = ({ existingFields, form, changeCallback, canEdit, attemptedSave, isExtendedEntity }) => {
    const fieldTypesEnum = isExtendedEntity === true
        ? Object.keys(FieldTypeEnum).filter(t => !RELATIONAL_FIELDS.includes(t))
        : Object.keys(FieldTypeEnum);

    const [t, i18n] = useTranslation();
    const [languages, setLanguages] = useState([]);

    const field = {
        ...form.field,
        ...form.typeSpecific
    }

    const isAlphanumeric = (input) => {
        const alphanumericRegex = /^[a-zA-Z0-9]+$/;
        return alphanumericRegex.test(input);
    }

    useEffect(() => {
        genericEntityService.getLanguages()
            .then(function (response) {
                setLanguages([
                    ...response.data
                ])
            })
            .catch(function (error) {
            });

    }, [])

    const getCodeError = () => {
        if (!form.field.code || form.field.code.trim() === '') {
            return t("NgeRequired");
        }
        if (existingFields.filter(f => f.FieldId !== field.FieldId).map(f => f.Code).includes(form.field.code)) {
            return t("NgeFieldCodeExists");
        }
        if (form.field.code && form.field.code.length > MAX_FIELD_CODE_LENGTH) {
            return t("NgeInvalidLength");
        }
        if (form.field.code && !isAlphanumeric(form.field.code)) {
            return t("NgeInvalidChar");
        }
        return null;
    };

    const getTypeError = () => {
        if (!form.field.type || form.field.type === null || form.field.type === undefined) {
            return t("NgeRequired");
        }
        return null;
    };

    const getCaptionError = () => {
        const hasCaptions = form.field.captions && Object.values(form.field.captions).some(caption => caption && caption.trim() !== '');
        if (!hasCaptions) {
            return t("NgeRequired");
        }

        if (form.field.captions) {
            const lengthErrors = {};
            Object.keys(form.field.captions).forEach(language => {
                if (form.field.captions[language]?.length > MAX_FIELD_CAPTION_LENGTH) {
                    lengthErrors[language] = t("NgeInvalidLength");
                }
            });
            if (Object.keys(lengthErrors).length > 0) {
                return lengthErrors;
            }
        }
        return null;
    };

    useEffect(() => {
        const codeError = getCodeError();
        if (form.errors.code !== codeError) {
            changeCallback({
                ...form,
                errors: {
                    ...form.errors,
                    code: codeError
                }
            });
        }
    }, [form.field.code, attemptedSave])

    useEffect(() => {
        const captionError = getCaptionError();
        if (JSON.stringify(form.errors.captions) !== JSON.stringify(captionError)) {
            changeCallback({
                ...form,
                errors: {
                    ...form.errors,
                    captions: captionError
                }
            });
        }
    }, [form.field.captions, attemptedSave])

    useEffect(() => {
        const typeError = getTypeError();
        if (form.errors.type !== typeError) {
            changeCallback({
                ...form,
                errors: {
                    ...form.errors,
                    type: typeError
                }
            });
        }
    }, [form.field.type, attemptedSave])

    useEffect(() => {
        if (form.typeSpecific.minValue === null || form.typeSpecific.maxValue === null) {
            return;
        }

        const min = form.typeSpecific.minValue;
        const max = form.typeSpecific.maxValue;

        if (min !== "" && max !== "" &&
            Number(form.typeSpecific.minValue) > Number(form.typeSpecific.maxValue)) {
            changeCallback({
                ...form,
                typeSpecificErrors: {
                    ...form.typeSpecificErrors,
                    range: t("NgeInvalidRange")
                }
            });
            return;
        } else {
            changeCallback({
                ...form,
                typeSpecificErrors: {
                    ...form.typeSpecificErrors,
                    range: null
                }
            });
        }
        return;
    }, [form.typeSpecific.minValue, form.typeSpecific.maxValue])

    useEffect(() => {
        if (form.typeSpecific.decimalDigits === null) {
            return;
        }
        if (form.typeSpecific.decimalDigits < 0 || form.typeSpecific.decimalDigits > 18) {
            changeCallback({
                ...form,
                typeSpecificErrors: {
                    ...form.typeSpecificErrors,
                    decimalDigits: t("NgeInvalidDecimalDigits")
                }
            });
            return;
        } else {
            changeCallback({
                ...form,
                typeSpecificErrors: {
                    ...form.typeSpecificErrors,
                    decimalDigits: null
                }
            });
        }
    }, [form.typeSpecific.decimalDigits])


    useEffect(() => {
        if (field.type === FieldTypeEnum.Text && field.textSize !== null && field.defaultValue &&
            typeof field.defaultValue === 'string' && field.defaultValue.length > TextFieldSizeEnum[field.textSize]) {
            changeCallback({
                ...form,
                typeSpecificErrors: {
                    ...form.typeSpecificErrors,
                    defaultValue: t("NgeInvalidLength")
                }
            });
            return;
        }

        changeCallback({
            ...form,
            typeSpecificErrors: {
                ...form.typeSpecificErrors,
                defaultValue: null
            }
        });
    }, [form.typeSpecific.textSize, form.typeSpecific.defaultValue])

    const handleTypeChange = (value) => {
        if (!canEdit) {
            return;
        }

        let defaultTypeSpecific = {};

        if (value === FieldTypeEnum.Text) {
            defaultTypeSpecific = { textSize: 'Medium' };
        } else if (value === FieldTypeEnum.Decimal || value === FieldTypeEnum.Currency) {
            defaultTypeSpecific = { decimalDigits: 2 };
        }

        changeCallback({
            ...form,
            field: {
                ...form.field,
                type: value
            },
            typeSpecific: defaultTypeSpecific
        });
    }

    const handleGenericChange = (property, value) => {
        if (!canEdit) {
            return;
        }
        changeCallback({
            ...form,
            field: {
                ...form.field,
                [property]: value
            }
        });
    }

    const handleCaptionChange = (value, language) => {
        if (!canEdit) {
            return;
        }

        const newCaptions = {
            ...form.field.captions,
            [language]: value
        };

        changeCallback({
            ...form,
            field: {
                ...form.field,
                captions: newCaptions
            }
        })
    }
    const handleSpecificTypeChange = (property, value) => {
        if (!canEdit) {
            return;
        }
        changeCallback({
            ...form,
            typeSpecific: {
                ...form.typeSpecific,
                [property]: value
            }
        })
    }

    const specificTypeForm = () => {

        switch (form.field.type) {
            case FieldTypeEnum.Integer:
                return <NumericForm propertyChangeCallback={handleSpecificTypeChange} isDecimalField={false} field={field} errors={form.typeSpecificErrors} canEdit={canEdit} />
            case FieldTypeEnum.Text:
                return <TextForm propertyChangeCallback={handleSpecificTypeChange} field={field} errors={form.typeSpecificErrors} canEdit={canEdit} />
            case FieldTypeEnum.Relation:
                return <RelationForm propertyChangeCallback={handleSpecificTypeChange} field={field} errors={form.typeSpecificErrors} canEdit={canEdit} />
            case FieldTypeEnum.Decimal:
                return <NumericForm propertyChangeCallback={handleSpecificTypeChange} isDecimalField={true} field={field} errors={form.typeSpecificErrors} canEdit={canEdit} />
            case FieldTypeEnum.Currency:
                return <CurrencyForm propertyChangeCallback={handleSpecificTypeChange} field={field} errors={form.typeSpecificErrors} canEdit={canEdit} />
            case FieldTypeEnum.Dropdown:
                return <DropdownForm propertyChangeCallback={handleSpecificTypeChange} field={field} languages={languages} canEdit={canEdit} />
            case FieldTypeEnum.Boolean:
                return <BooleanForm propertyChangeCallback={handleSpecificTypeChange} field={field} errors={form.typeSpecificErrors} canEdit={canEdit} />
            case FieldTypeEnum.Datetime:
                return <DatetimeForm propertyChangeCallback={handleSpecificTypeChange} field={field} errors={form.typeSpecificErrors} canEdit={canEdit} />
            case FieldTypeEnum.Date:
                return <DateForm propertyChangeCallback={handleSpecificTypeChange} field={field} errors={form.typeSpecificErrors} canEdit={canEdit} />
            case FieldTypeEnum.Time:
                return <TimeForm propertyChangeCallback={handleSpecificTypeChange} field={field} errors={form.typeSpecificErrors} canEdit={canEdit} />
            case FieldTypeEnum.Attachment:
                return <AttachmentForm propertyChangeCallback={handleSpecificTypeChange} field={field} errors={form.typeSpecificErrors} canEdit={canEdit} />
            default:
                return <></>
        }
    }

    return (
        <div className='field-form-grid'>
            <div className='field-two-columns'>
                <M_InputTextField
                    label={t("NgeCode")}
                    value={field.code ?? ""}
                    required
                    helperTextMessage={attemptedSave ? getCodeError() : null}
                    disabled={!canEdit}
                    color={attemptedSave && getCodeError() ? 'var(--color-red)' : null}
                    onChange={(e) => handleGenericChange('code', e.target.value)}
                />
            </div>
            <div className='field-one-columns'>
                <M_SwitchButton backgroundColor={canEdit ? 'var(--color-white)' : 'var(--color-grey-light)'} checked={field.isMandatory ?? false} color={"var(--color-blue)"} label={<div className='text'>{t("NgeMandatory")}</div>} onClick={(e) => handleGenericChange("isMandatory", e.target.checked)} />
            </div>
            <div className='field-one-columns'>
                <M_SwitchButton backgroundColor={canEdit ? 'var(--color-white)' : 'var(--color-grey-light)'} checked={field.isUnique ?? false} color={"var(--color-blue)"} label={<div className='text'>{t("NgeUnique")}</div>} onClick={(e) => handleGenericChange("isUnique", e.target.checked)} />
            </div>
            {
                languages.map(l => {
                    const captionValue = form.field.captions?.[l.LanguageCode] ?? "";
                    const captionError = getCaptionError();
                    const langError = typeof captionError === 'object' && captionError?.[l.LanguageCode] ? captionError[l.LanguageCode] :
                        (typeof captionError === 'string' ? captionError : null);

                    return (
                        <div key={l.LanguageCode} className={`field-two-columns ${canEdit ? '' : 'disabled'}`}>
                            <M_InputTextField
                                disabled={!canEdit}
                                label={`${t("NgeCaption")} ${t(l.LanguageCode)}`}
                                helperTextMessage={attemptedSave ? langError : null}
                                color={attemptedSave && langError ? 'var(--color-red)' : null}
                                value={captionValue}
                                onChange={(e) => handleCaptionChange(e.target.value, l.LanguageCode)}
                            />
                        </div>
                    )
                })
            }
            <div className='field-two-columns'>
                <M_InputTextField
                    label={t("NgeDescription")}
                    value={field.description ?? ""}
                    helperTextMessage={form.errors['description']}
                    disabled={!canEdit}
                    color={form.errors["description"] === null ? null : 'var(--color-red)'}
                    onChange={(e) => handleGenericChange('description', e.target.value)}
                />
            </div>
            <div className='field-two-columns'>
                <M_Select
                    label={t("NgeType")}
                    value={field.type ?? ""}
                    options={fieldTypesEnum.map(el => ({
                        label: el,
                        value: FieldTypeEnum[el]
                    })
                    )}
                    color={attemptedSave && getTypeError() ? 'var(--color-red)' : null}
                    required
                    noNone
                    disabled={field.fieldId !== null}
                    onChange={(e) => handleTypeChange(e.target.value)}
                />
                <FormHelperText>{attemptedSave ? getTypeError() : null}</FormHelperText>
            </div>
            {
                specificTypeForm()
            }
        </div>
    )
}
